import { useEffect, useState } from "react";
import { http, nf } from "@/lib/api";
import { PageHeader, Stat } from "@/components/Ui";
import ExportButtons from "@/components/ExportButtons";
import AiInsight from "@/components/AiInsight";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";

export default function ReportAccuracy() {
  const [d, setD] = useState(null);

  useEffect(() => {
    http.get("/reports/accuracy").then((r) => setD(r.data)).catch(() => setD({ rows: [], per_cabang: [] }));
  }, []);

  if (!d) return <div className="p-8 mono text-sm">Memuat laporan…</div>;

  return (
    <div data-testid="report-accuracy-page">
      <PageHeader subtitle="Laporan" title="Proyeksi vs Realisasi Penjualan" testId="report-accuracy-header">
        <ExportButtons kind="accuracy" testId="export-accuracy" />
      </PageHeader>

      <div className="p-6 sm:p-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Stat testId="accuracy-total-proyeksi" label="Total Qty Proyeksi" value={nf(d.total_proyeksi)} />
          <Stat testId="accuracy-total-realisasi" label="Total Qty Realisasi" value={nf(d.total_realisasi)} accent="#16A34A" />
          <Stat testId="accuracy-nasional" label="Akurasi Nasional" value={`${d.akurasi_nasional}%`}
            sub={`pembanding: ${(d.periode_pembanding || []).join(", ")}`} accent="#4338CA" />
        </div>

        <div className="erp-panel p-4">
          <div className="mono text-[11px] uppercase tracking-widest text-[#525252] mb-2">
            Proyeksi vs Realisasi per Cabang
          </div>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.per_cabang}>
                <CartesianGrid stroke="#eee" vertical={false} />
                <XAxis dataKey="cabang" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={60} interval={0} />
                <YAxis tick={{ fontSize: 10 }} width={55} />
                <Tooltip formatter={(v) => nf(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="proyeksi" name="Proyeksi" fill="#4338CA" />
                <Bar dataKey="realisasi" name="Realisasi" fill="#16A34A" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="erp-panel overflow-auto" data-testid="accuracy-branch-table-wrap">
          <table className="erp-table">
            <thead>
              <tr><th>Cabang</th><th className="num">Produk Diusulkan</th><th className="num">Qty Proyeksi</th>
                <th className="num">Qty Realisasi</th><th className="num">Akurasi</th></tr>
            </thead>
            <tbody>
              {d.per_cabang.map((c) => (
                <tr key={c.cabang} data-testid={`accuracy-branch-row-${c.cabang}`}>
                  <td className="font-medium">{c.cabang}</td>
                  <td className="num">{nf(c.jumlah_produk)}</td>
                  <td className="num">{nf(c.proyeksi)}</td>
                  <td className="num">{nf(c.realisasi)}</td>
                  <td className="num font-semibold" style={{ color: c.akurasi >= 70 ? "#16A34A" : c.akurasi >= 40 ? "#B45309" : "#b91c1c" }}>
                    {c.akurasi}%
                  </td>
                </tr>
              ))}
              {d.per_cabang.length === 0 && (
                <tr><td colSpan={5} className="text-center text-neutral-500 py-6">Belum ada usulan proyeksi</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="erp-panel overflow-auto max-h-[60vh]" data-testid="accuracy-detail-table-wrap">
          <table className="erp-table">
            <thead>
              <tr><th>Cabang</th><th>Kode Stock</th><th>Deskripsi</th><th className="num">Proyeksi</th>
                <th className="num">Realisasi</th><th className="num">Selisih</th><th className="num">Akurasi</th><th>Keterangan</th></tr>
            </thead>
            <tbody>
              {d.rows.map((r, i) => (
                <tr key={i} data-testid={`accuracy-row-${r.nama_cabang}-${r.kode_stock}`}>
                  <td>{r.nama_cabang}</td>
                  <td className="mono font-medium">{r.kode_stock}</td>
                  <td className="max-w-[220px] truncate">{r.deskripsi_produk}</td>
                  <td className="num">{nf(r.qty_proyeksi)}</td>
                  <td className="num">{nf(r.qty_realisasi)}</td>
                  <td className="num" style={{ color: r.selisih < 0 ? "#b91c1c" : "#16A34A" }}>{nf(r.selisih)}</td>
                  <td className="num font-semibold">{r.akurasi}%</td>
                  <td>{r.keterangan}</td>
                </tr>
              ))}
              {d.rows.length === 0 && (
                <tr><td colSpan={8} className="text-center text-neutral-500 py-6">Belum ada data pembanding</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <AiInsight scope="akurasi-proyeksi" />
      </div>
    </div>
  );
}
